import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, Info, Copy, Check, User, Building } from '../components/Icons';
import toast from 'react-hot-toast';

const Login = () => {
  // Mode state
  const [isLoginMode, setIsLoginMode] = useState(true);

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Signup form state
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    business_name: ''
  });
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);

  // Common state
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const { login, register } = useAuth();

  const demoCredentials = [
    {
      type: "Demo Business",
      email: "demo@posfbr.com",
      password: "FbrDemo#2026Test!",
      description: "Full access to all features with sample data"
    },
    {
      type: "Test Tenant",
      email: "test@company.com",
      password: "test123",
      description: "Sample business tenant with products and clients"
    }
  ];

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`${field} copied to clipboard!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    return errors;
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    // Password validation
    const passwordErrors = validatePassword(signupData.password);
    if (passwordErrors.length > 0) {
      passwordErrors.forEach(error => toast.error(error));
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const result = await register({
        name: signupData.name,
        email: signupData.email,
        password: signupData.password,
        business_name: signupData.business_name
      });

      if (result.success) {
        toast.success('Registration successful! Please sign in.');
        setIsLoginMode(true);
        // Clear signup form
        setSignupData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          business_name: ''
        });
      }
      // Error handling is done in AuthContext, no need for duplicate toast
    } catch (error) {
      console.error('Registration error:', error);
      // Error toast already shown by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleSignupChange = (e) => {
    setSignupData({
      ...signupData,
      [e.target.name]: e.target.value
    });
  };

  const fillDemoCredentials = (cred) => {
    if (isLoginMode) {
      setEmail(cred.email);
      setPassword(cred.password);
    } else {
      setSignupData({
        ...signupData,
        email: cred.email,
        password: cred.password,
        confirmPassword: cred.password
      });
    }
    toast.success(`Filled ${cred.type} credentials!`);
  };

  const switchMode = () => {
    setIsLoginMode(!isLoginMode);
    // Clear forms when switching
    setEmail('');
    setPassword('');
    setSignupData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      business_name: ''
    });
    setShowPassword(false);
    setShowSignupPassword(false);
    setShowSignupConfirmPassword(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-amber-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-slate-900">
            {isLoginMode ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-600">
            {isLoginMode ? (
              <>
                Or{' '}
                <button
                  onClick={switchMode}
                  className="font-medium text-amber-600 hover:text-amber-700"
                >
                  create a new account
                </button>
              </>
            ) : (
              <>
                Or{' '}
                <button
                  onClick={switchMode}
                  className="font-medium text-amber-600 hover:text-amber-700"
                >
                  sign in to your existing account
                </button>
              </>
            )}
            {' '}or{' '}
            <Link
              to="/"
              className="font-medium text-amber-600 hover:text-amber-700"
            >
              return to home page
            </Link>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Authentication Form */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-slate-900">
                {isLoginMode ? 'Login Form' : 'Signup Form'}
              </h3>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isLoginMode ? 'bg-amber-600' : 'bg-slate-300'}`}></div>
                <div className={`w-2 h-2 rounded-full ${!isLoginMode ? 'bg-amber-600' : 'bg-slate-300'}`}></div>
              </div>
            </div>

            {isLoginMode ? (
              // Login Form
              <form className="space-y-6" onSubmit={handleLoginSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 pr-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              // Signup Form
              <form className="space-y-6" onSubmit={handleSignupSubmit}>
                <div>
                  <label htmlFor="signup-name" className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="signup-name"
                      name="name"
                      type="text"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your full name"
                      value={signupData.name}
                      onChange={handleSignupChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="signup-email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="signup-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your email"
                      value={signupData.email}
                      onChange={handleSignupChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="signup-business" className="block text-sm font-medium text-slate-700 mb-2">
                    Business Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="signup-business"
                      name="business_name"
                      type="text"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your business name"
                      value={signupData.business_name}
                      onChange={handleSignupChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="signup-password" className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                    <span className="text-xs text-slate-500 ml-2">(8+ chars, uppercase, special char)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="signup-password"
                      name="password"
                      type={showSignupPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 pr-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your password"
                      value={signupData.password}
                      onChange={handleSignupChange}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                    >
                      {showSignupPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {signupData.password && (
                    <div className="mt-2">
                      <div className="flex space-x-2 mb-2">
                        <div className={`h-2 flex-1 rounded-full ${signupData.password.length >= 8 ? 'bg-green-500' : 'bg-slate-200'}`}></div>
                        <div className={`h-2 flex-1 rounded-full ${/[A-Z]/.test(signupData.password) ? 'bg-green-500' : 'bg-slate-200'}`}></div>
                        <div className={`h-2 flex-1 rounded-full ${/[!@#$%^&*(),.?":{}|<>]/.test(signupData.password) ? 'bg-green-500' : 'bg-slate-200'}`}></div>
                      </div>
                      <div className="text-xs text-slate-600">
                        <div className={`${signupData.password.length >= 8 ? 'text-green-600' : 'text-slate-500'}`}>
                          ✓ At least 8 characters
                        </div>
                        <div className={`${/[A-Z]/.test(signupData.password) ? 'text-green-600' : 'text-slate-500'}`}>
                          ✓ One uppercase letter
                        </div>
                        <div className={`${/[!@#$%^&*(),.?":{}|<>]/.test(signupData.password) ? 'text-green-600' : 'text-slate-500'}`}>
                          ✓ One special character
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-slate-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="signup-confirm-password"
                      name="confirmPassword"
                      type={showSignupConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 pl-10 pr-10 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 focus:z-10 sm:text-sm"
                      placeholder="Confirm your password"
                      value={signupData.confirmPassword}
                      onChange={handleSignupChange}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                    >
                      {showSignupConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Demo Credentials */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8">
            <div className="flex items-center mb-6">
              <Info className="h-6 w-6 text-amber-600 mr-2" />
              <h3 className="text-lg font-medium text-slate-900">Demo Credentials</h3>
            </div>
            <p className="text-sm text-slate-600 mb-6">
              Use these demo accounts to explore the system. All data is sample data and will be reset periodically.
            </p>

            <div className="space-y-4">
              {demoCredentials.map((cred, index) => (
                <div key={index} className="border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-amber-200 transition-all duration-300">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-slate-900">{cred.type}</h4>
                    <button
                      onClick={() => fillDemoCredentials(cred)}
                      className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-700 px-2.5 py-1 rounded-full font-medium transition-colors duration-300"
                    >
                      Use This
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Email:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{cred.email}</span>
                        <button
                          onClick={() => copyToClipboard(cred.email, 'email')}
                          className="text-slate-400 hover:text-slate-600 transition duration-300"
                        >
                          {copiedField === 'email' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Password:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{cred.password}</span>
                        <button
                          onClick={() => copyToClipboard(cred.password, 'password')}
                          className="text-slate-400 hover:text-slate-600 transition duration-300"
                        >
                          {copiedField === 'password' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mt-2">{cred.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <h4 className="text-sm font-medium text-amber-900 mb-2">💡 Quick Start Tips:</h4>
              <ul className="text-xs text-amber-800 space-y-1">
                <li>• Click "Use This" to auto-fill credentials</li>
                <li>• Use "Demo Business" for full feature access</li>
                <li>• "Admin Account" for system administration</li>
                <li>• All data is reset daily for demo purposes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center">
          <p className="text-sm text-slate-600">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@posfbr.com" className="text-amber-600 hover:text-amber-700">
              support@posfbr.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
